import classnames from 'classnames'
import QueryString from 'qs'
import {
  append,
  clone,
  innerJoin,
  prop
} from 'ramda'
import React,
{
  Component,
  Fragment
} from 'react'

import { EmptyState } from '@driftt/crm-components'
import {
  ActionDropdown,
  Div,
  NavigationHeader,
  TextLoader
} from '@driftt/tide-core'
import { Table } from '@driftt/tide-table'
import { tracking } from '@driftt/traffic-control'
import { createFastlanePlaybook } from 'api/playbooks'
import {
  archiveSegments,
  getContactSegments,
  getContactSegmentsByTypeAndName,
  getContactSegmentsByType,
  getContactSegmentsByNameQuery
} from 'api/segments'
import Paginator from 'components/Paginator'
import SegmentControls from 'components/Segments/SegmentControls'
import Config from 'config'
import {
  getOrgMarketoAccessType,
  MarketoAccessTypes,
  shouldShowMarketoEducation,
  shouldShowMarketoImports
} from 'utils/marketoAccess'

import MarketoSegmentsEmptyState from '../MarketoEducation/MarketoSegmentsEmptyState'
import { AllSegmentsConstants } from './constants/AllSegmentsConstants'
import { getSegmentTypesAvailableForFiltering } from './constants/SegmentTypes'
import DeleteSegmentsConfirmationModal from './DeleteSegmentsConfirmationModal'
import EnrollSegmentInPlaybook from './EnrollSegmentInPlaybook'
import EnrollSegmentSuccessModal from './EnrollSegmentSuccessModal'
import { AllSegmentsFormatter } from './RowFormatters/AllSegmentsFormatter'

import './style.css'

class AllSegments extends Component {
  constructor(props) {
    super(props)

    this.state = {
      segments: null,
      isLoading: true,
      users: null,
      size: 25,
      index: 0,
      currentPage: 0,
      totalPages: null,
      totalSegments: 0,
      filterValue: '',
      allSelected: false,
      typeFiltered: false,
      firstLoad: true,
      segmentToTargetPlaybook: null,
      segmentIdsToDelete: [],
      isTargetModalOpen: false,
      isDeleteConfirmationModalOpen: false,
      isCreatingFastlanePlaybook: false,
    }
  }

  componentDidMount() {
    const {
      onFilterSegmentType,
      location
    } = this.props
    tracking.trackEvent('Contact Segments - Page Viewed')

    const rawQueryString = location.search
    const params = QueryString.parse(rawQueryString, { ignoreQueryPrefix: true })
    const selectedSegmentType = params.type && params.type.toUpperCase()

    const filterableSegments = getSegmentTypesAvailableForFiltering().map(prop('key'))
    const isValidSegmentType = !!selectedSegmentType && filterableSegments.includes(selectedSegmentType)

    if (isValidSegmentType) {
      onFilterSegmentType(selectedSegmentType)
    } else {
      onFilterSegmentType('ALL')
      this.getSegments()
    }
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevProps.filteredSegmentType !== this.props.filteredSegmentType && this.props.filteredSegmentType !== 'ALL') {
      this.setState({ typeFiltered: true})
      this.getSegments()
    }

    if (prevProps.filteredSegmentType !== this.props.filteredSegmentType && this.props.filteredSegmentType === 'ALL') {
      this.setState({ typeFiltered: false })
      this.getSegments()
    }
  }

  get hydratedSegmentsToDelete() {
    const { segments } = this.props
    const { segmentIdsToDelete } = this.state

    return innerJoin((segment, selectedSegmentId) => segment.id === selectedSegmentId,
      segments,
      segmentIdsToDelete)
  }

  getSegments = async () => {
    const {
      users,
      onReceiveSegments,
      filteredSegmentType
    } = this.props

    const {
      size,
      index,
      filterValue
    } = this.state

    try {
      const {
        data: {
          total,
          userList
        }
      } = filteredSegmentType === 'ALL' ? (
        filterValue === '' ?
          await getContactSegments(size, index) :
          await getContactSegmentsByNameQuery(filterValue, size, index)
        ) : (
        filterValue === '' ?
          await getContactSegmentsByType(size, index, filteredSegmentType) :
          await getContactSegmentsByTypeAndName(size, index, filteredSegmentType, filterValue)
      )

      onReceiveSegments(userList)

      this.setState({
        isLoading: false,
        users: users,
        totalSegments: total,
        firstLoad: false,
        totalPages: Math.ceil(total / size)
      })

    } catch(e) {
      console.log(e)
    }
  }

  previousPage = () => {
    const {
      size,
      index,
    } = this.state

    const newIndex = index - size

    this.setState(prevState => {
      const nextPage = prevState.currentPage - 1
      return {
        currentPage: nextPage,
        index: newIndex,
        isLoading: true
      }
    }, this.getSegments)
  }

  nextPage = () => {
    const {
      size,
      index,
    } = this.state

    const newIndex = index + size

    this.setState(prevState => {
      const nextPage = prevState.currentPage + 1
      return {
        currentPage: nextPage,
        index: newIndex,
        isLoading: true
      }
    }, this.getSegments)
  }

  setStickyHeader = (event) => {
    const { hasScrolled } = this.state
    const scrollingElement = event.target

    const offset = scrollingElement.scrollTop

    if (!hasScrolled && offset > 20) {
      this.setState({ hasScrolled: true })
    } else if (hasScrolled && offset === 0) {
      this.setState({ hasScrolled: false })
    }
  }

  closeDeleteSegmentsModal = () => {
    this.setState({ isDeleteConfirmationModalOpen: false })
  }

  promptToDeleteSingleSegment = (id) => {
    this.setState({
      isDeleteConfirmationModalOpen: true,
      segmentIdsToDelete: [id]
    })
  }

  promptToDeleteSelectedSegments = () => {
    const { selectedSegments } = this.props

    this.setState({
      isDeleteConfirmationModalOpen: true,
      segmentIdsToDelete: selectedSegments
    })
  }

  deleteSelectedSegments = async () => {
    const { showTransientNotification } = this.props
    const { segmentIdsToDelete } = this.state

    this.setState({ isLoading: true })

    try {
      await archiveSegments(segmentIdsToDelete)
    } catch (e) {
      console.log(e)
    }

    this.setState({
      isDeleteConfirmationModalOpen: false,
      segmentIdsToDelete: []
    })

    showTransientNotification({
      type: 'success',
      message: `${segmentIdsToDelete.length} segments successfully deleted`
    })
    this.clearSelection()
    this.getSegments()
  }

  filterSegments = (event) => {
    const value = event.target.value

    this.setState({
      filterValue: value,
      isLoading: true
    }, this.getSegments)
  }

  changeSize = ({ value }) => {
    this.setState({
      size: value,
      isLoading: true
    }, this.getSegments)
  }

  selectSegment = (id, isSelected) => {
    const {
      onSelectSegment,
      onDeselectSegment
    } = this.props

    if (isSelected) {
      onSelectSegment(id)
    } else {
      onDeselectSegment(id)
    }
  }

  clearSelection = () => {
    const { onSelectSegment } = this.props

    onSelectSegment([])

    this.setState({ allSelected: false })
  }

  selectAll = (isSelected) => {
    const {
      segments,
      onSelectSegment,
      selectedSegments,
      onDeselectSegment
    } = this.props

    const selected = clone(selectedSegments)

    segments.forEach(segment => selected.push(segment.id))

    if (isSelected) {
      onSelectSegment(selected)
    } else {
      onDeselectSegment(selected)
    }

    this.setState({ allSelected: isSelected })
  }

  goToCreateFilter = () => {
    const { history } = this.props
    tracking.trackEvent('Segment List - New Segment Clicked')

    history.push('/?createSegment=true')
  }

  goToMarketoImport = () => {
    const { history } = this.props
    tracking.trackEvent('Segment List - New Segment (Marketo) Clicked')

    if (getOrgMarketoAccessType() === MarketoAccessTypes.HAS_ACCESS_CONNECTED) {
      history.push('/imports/marketo')
    } else {
      history.push('/imports')
    }
  }

  createFastlanePlaybook = async ({ segment }) => {
    this.setState({
      segmentToTargetPlaybook: segment,
      isCreatingFastlanePlaybook: true
    })

    const defaultPlaybookName = `Playbook for '${segment.name}'`
    const response = await createFastlanePlaybook({
      segmentId: segment.id,
      name: defaultPlaybookName
    })

    this.setState({ isCreatingFastlanePlaybook: false })

    const playbookUrl = response.playbookUrl
    window.open(playbookUrl, '_blank')
  }

  targetPlaybook = ({
    segment: targetSegmentForPlaybook,
    createNew = false
  }) => {
    if (createNew) {
      tracking.trackEvent('Contact Segments - Clicked Create Fastlane Playbook', { page: 'All Segments', })

      window.open('/playbooks', '_blank')

      // this.createFastlanePlaybook({ segment: targetSegmentForPlaybook })
    } else {
      tracking.trackEvent('Contact Segments - Clicked Add To Playbook', {
        page: 'All Segments',
        experiment: 'show_button',
      })

      this.setState(prevState => ({
        segmentToTargetPlaybook: targetSegmentForPlaybook,
        isTargetModalOpen: !prevState.isTargetModalOpen
      }))
    }
  }

  onCloseTargetPlaybookModal = () => {
    this.targetPlaybook({ segment: null })
  }

  onSaveAudienceSuccess = (pbId) => {
    this.setState({ playbookEdited: pbId }, this.toggleSuccessModal)
  }

  toggleSuccessModal = () => {
    this.setState(prevState => ({ successfulSave: !prevState.successfulSave }))
  }

  goToPlaybook = () => {
    const { playbookEdited } = this.state

    window.location = `${Config.PLAYBOOK_EDITOR_URL}/${playbookEdited}`
  }

  renderEmptyState = () => {
    const {
      filteredSegmentType,
      history
    } = this.props

    if (filteredSegmentType === 'MARKETO' && shouldShowMarketoEducation()) {
      return <MarketoSegmentsEmptyState history={history} />
    }

    return (
      <EmptyState
        history={history}
        topText="Looks like you don’t have any segments yet."
        bottomText="Segments are groups of contacts that fit certain criteria determined by you. Use segments to define playbook audience, export groups of contacts, and more."
        ctaUrl="/contacts/?createSegment=true"
        ctaText="Create new segment"
      />
    )
  }

  render() {
    const {
      segments,
      selectedSegments,
      history,
      users
    } = this.props

    const {
      allSelected,
      currentPage,
      firstLoad,
      hasScrolled,
      isLoading,
      isCreatingFastlanePlaybook,
      isDeleteConfirmationModalOpen,
      segmentToTargetPlaybook,
      size,
      successfulSave,
      isTargetModalOpen,
      totalPages,
      totalSegments,
    } = this.state


    const newContactSegmentAction = {
      label: 'New contact segment',
      icon: 'users',
      action: hideDropdown => {
        hideDropdown()
        this.goToCreateFilter()
      }
    }

    const marketoListAction = {
      label: 'Import Marketo List',
      icon: 'sync',
      action: hideDropdown => {
        hideDropdown()
        this.goToMarketoImport()
      }
    }

    let newSegmentActions = [
      newContactSegmentAction
    ]

    if (shouldShowMarketoImports()) {
      newSegmentActions = append(marketoListAction, newSegmentActions)
    }

    return (
      <Fragment>
        <NavigationHeader title="Contact segments">
          <Div className="contacts-segments--bulk-actions">
            <ActionDropdown align="right" title={'Create segment'}
              actions={newSegmentActions} buttonType="primary" />
          </Div>
        </NavigationHeader>
        <Div className="contacts-segments-filter-controls">
          <SegmentControls totalSegments={totalSegments} onSearch={this.filterSegments}
            onDelete={this.promptToDeleteSelectedSegments} onSelectAll={this.selectAll}/>
        </Div>
        <Div className={classnames('contacts-segments-list-container', { 'contacts-segments-list--stick': hasScrolled })} onScroll={this.setStickyHeader}>
          {(!firstLoad) &&
            <Table
              isWorking={isLoading}
              columns={AllSegmentsConstants(this.selectAll, allSelected, this.updateColumnOrder)}
              data={!!segments && segments.map(segment => (
                AllSegmentsFormatter({
                  segment,
                  users,
                  selectedSegments,
                  history,
                  isLoading: isCreatingFastlanePlaybook && segment.id === segmentToTargetPlaybook.id,
                  onDelete: this.promptToDeleteSingleSegment,
                  onSelect: this.selectSegment,
                  onTarget: this.targetPlaybook,
                })
              ))}
              className="contacts-segments-list"
            />
          }
          {(segments.length === 0 && !isLoading) &&
            this.renderEmptyState()
          }
          {!!segments &&
            <Paginator
              totalPages={totalPages}
              size={size}
              goBackward={this.previousPage}
              goForward={this.nextPage}
              changeSize={this.changeSize}
              index={updateCurrentPage(currentPage, totalSegments)}
            />
          }
          <EnrollSegmentInPlaybook
            open={isTargetModalOpen}
            segment={segmentToTargetPlaybook}
            onClose={this.onCloseTargetPlaybookModal}
            onSaveSuccess={this.onSaveAudienceSuccess}
          />
          <EnrollSegmentSuccessModal
            open={successfulSave}
            onClose={this.toggleSuccessModal}
            goToPlaybook={this.goToPlaybook}
          />
          <DeleteSegmentsConfirmationModal
            open={isDeleteConfirmationModalOpen}
            segmentsToDelete={this.hydratedSegmentsToDelete}
            onConfirm={this.deleteSelectedSegments}
            onClose={this.closeDeleteSegmentsModal}
          />
        </Div>
        {isLoading && <TextLoader text="Grabbing your segments…"/>}
      </Fragment>
    )
  }
}

export default AllSegments


export const updateCurrentPage = (currentPage, total) => {
  const newCurrentPage = (total ===  0  ?
    (currentPage === -1 ? 0 : -1)
    : currentPage)

  return newCurrentPage
}
